/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {useState, useEffect, useMemo} from 'react'
import clsx from 'clsx'
import * as Yup from 'yup'
import swal from 'sweetalert'
import {useFormik} from 'formik'
import {useDispatch} from 'react-redux'
// import ReactTooltip from 'react-tooltip'

import {Timezone, TimezoneValue} from '../../../config/data'
import {setUserInformation} from '../../../setup/redux/actions/webshop'
import {BACKEND_API_V2} from '../../../config/config'
import {useTranslation} from 'react-i18next'
import {VatEstimate} from '../../../utils/vatApi'
import {vatFieldState, VatCheckStatus} from '../../../utils/vatFieldState'
import {BusinessVatFields, MemberType} from './BusinessVatFields'
import {sortCountriesByLocale} from '../../../utils/countryNames'

const baseInitialValues = {
  username: '',
  firstname: '',
  secondname: '',
  email: '',
  password: '',
  changepassword: '',
  countryId: 0,
  streetaddress: '',
  suite: '',
  cityaddress: '',
  postalcode: '',
  timezone: '(GMT-11:00) International Date Line West',
  birth: '2022-01-01',
  // Personal/business + UST-ID (Task 3). `vatStatus`/`vatChecking` are not sent anywhere — they
  // only exist so the Yup schema below can gate completion on the live VIES result, the same way
  // BusinessVatFields gates its own rendering (see vatFieldState).
  memberType: 'personal' as MemberType,
  company_name: '',
  vat_id: '',
  vatStatus: 'idle' as VatCheckStatus,
  vatChecking: false,
}

const curDate = new Date()
const minDate = new Date(curDate.getFullYear() - 18, curDate.getMonth(), curDate.getDate())
export const registrationSchema = Yup.object().shape({
  username: Yup.string()
    .min(6, 'Minimum 6 symbols')
    .max(30, 'Maximum 30 symbols')
    .required('User Name is required'),
  firstname: Yup.string()
    .min(1, 'Minimum 1 symbols')
    .max(30, 'Maximum 30 symbols')
    .required('First name is required'),
  email: Yup.string()
    .email('Wrong email format')
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Email is required'),
  secondname: Yup.string()
    .min(1, 'Minimum 1 symbols')
    .max(30, 'Maximum 30 symbols')
    .required('Second name is required'),
  password: Yup.string()
    // Keycloak enforces a 12-char minimum under SSO — validate it HERE (data-entry step) so the user
    // isn't rejected only at the final sign_up call (which they can't go back from in the webshop flow).
    .min(12, 'Minimum 12 characters')
    .max(24, 'Maximum 24 symbols')
    .test(
      'Password Warning',
      "Must be at least 12 characters and contain an uppercase and lowercase letter, a number and a special character.   Special characters: !#$%&'()*+,-./:;<=>?@[]^_`{|}~",
      (password?: string | '') => {
        return /^(?=.{12,}$)(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[\W_]+).*$/.test(
          password ? password : ''
        )
      }
    )
    .required('Password is required'),
  changepassword: Yup.string()
    .required('Password confirmation is required')
    .when('password', {
      is: (val: string) => (val && val.length > 0 ? true : false),
      then: Yup.string().oneOf([Yup.ref('password')], "Password and Confirm Password didn't match"),
    }),
  countryId: Yup.number()
    .min(1, 'Please select a country')
    .required('Country is required'),
  streetaddress: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(100, 'Maximum 100 symbols')
    .required('Street Address is required'),
  suite: Yup.string()
    .min(1, 'Minimum 1 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Apt/Suite is required'),
  cityaddress: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(100, 'Maximum 100 symbols')
    .required('City is required'),
  postalcode: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('ZIP/Postal code is required'),
  birth: Yup.date()
    .required('Birthday is incorrect')
    .max(minDate, 'Please enter your exact birthday. You must be at least 18 years of age'),
  memberType: Yup.string().oneOf(['personal', 'business']).required(),
  // A UST-ID means you're a business — company name and the UST-ID are only required once
  // "Business" is picked (coupled, per Q4 of the grilling-settled requirements).
  company_name: Yup.string().when('memberType', {
    is: 'business',
    then: Yup.string().min(1, 'Company name is required').required('Company name is required'),
  }),
  vat_id: Yup.string().when('memberType', {
    is: 'business',
    then: Yup.string().required('UST-ID is required'),
  }),
  // Gates on the live VIES result via the exact same pure decision BusinessVatFields uses to
  // render its own feedback — `invalid` and `checking` (in-flight) block, `valid`/`unavailable`
  // don't. See src/utils/vatFieldState.ts.
  vatStatus: Yup.mixed().when(['memberType', 'vatChecking'], ((
    memberType: string,
    vatChecking: boolean,
    schema: any
  ) => {
    if (memberType !== 'business') return schema
    return schema.test(
      'vies-gate',
      'VAT validation must complete before continuing',
      (value: VatCheckStatus) => !vatFieldState(value, Boolean(vatChecking)).blocks
    )
  }) as any),
})

interface Props {
  setViewStatus: React.Dispatch<React.SetStateAction<number>>
  state: {
    countrieSettings: any[]
    paymentSettings: any[]
    basePlans: any[]
    addons: any[]
    affiliatePlans: any[]
    vatEstimate?: VatEstimate | null
  }
}

/**
 * Resolves the IP-estimated VAT country to a matching ACTIVE entry in the operating-countries
 * list, so the address-step can be defaulted by BOTH id and display name — Yup's `country` field
 * requires the name string (set today only by the manual `updateCountry` onChange), and the
 * `<select>` is matched by id. Returns `null` (no default) when there's no estimate, no
 * `country_id`, or the id doesn't match any active operating country — the member then picks
 * manually, exactly as before this feature existed.
 */
export function defaultCountryFromEstimate(
  estimate: Pick<VatEstimate, 'country_id'> | null | undefined,
  countries: Array<{id: number | string; country_name: string; status: number}> | null | undefined
): {countryId: number; country: string} | null {
  if (!estimate?.country_id) return null
  const match = countries?.find((c) => c.status === 1 && Number(c.id) === estimate.country_id)
  if (!match) return null
  return {countryId: Number(match.id), country: match.country_name}
}

export function Personinfo({setViewStatus, state}: Props) {
  const {t, i18n} = useTranslation()

  const dispatch = useDispatch()
  const [showPassword, setShowPassword] = useState(false)
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false)
  const [curCountries, setCurCountries] = useState<Array<any>>([])

  useEffect(() => {
    if (!state.countrieSettings) return
    setCurCountries(state.countrieSettings)
  }, [state.countrieSettings])

  // Active operating countries, localized to the UI language and sorted in that language's
  // alphabetical order. Recomputed when the list OR the language changes — without this the
  // dropdown keeps the English names/order after a language switch.
  const localizedCountries = useMemo(
    () => sortCountriesByLocale(curCountries.filter((c) => c.status === 1), i18n.language),
    [curCountries, i18n.language]
  )

  // Default the address-step country from the IP-estimated VAT lookup when it resolved before
  // the member reached this step (Personinfo mounts several steps after shopping.tsx kicks off
  // the estimate fetch). `state.vatEstimate` is null while loading or on a failed lookup, and
  // `defaultCountryFromEstimate` returns null whenever the id doesn't match an active operating
  // country — both fall back to the previous "no default" behaviour, same as today.
  const countryDefault = defaultCountryFromEstimate(state.vatEstimate, state.countrieSettings)
  const formik = useFormik({
    initialValues: {
      ...baseInitialValues,
      ...(countryDefault ?? {}),
    },
    validationSchema: registrationSchema,
    onSubmit: (values, {setSubmitting}) => {
      checkInformation(values)
    },
  })

  // Account type (and the VIES status it gates) flips WHICH fields the schema requires — business
  // adds company name, UST-ID and a non-blocking VIES result. Formik does not re-validate the rest
  // of the form when only `memberType` changes, which left the Continue button in a stale
  // enabled/disabled state until a field was touched. Force a full re-validation on those changes.
  useEffect(() => {
    formik.validateForm()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.memberType, formik.values.vatStatus, formik.values.vatChecking])

  // Log validation state whenever it changes
  useEffect(() => {
    console.log('=== FORM VALIDATION STATE ===')
    console.log('Form is valid:', formik.isValid)
    console.log('Form values:', formik.values)
    console.log('Form errors:', formik.errors)
    console.log('Form touched:', formik.touched)

    // Check each required field
    const fieldChecks = {
      username: {
        value: formik.values.username,
        hasError: !!formik.errors.username,
        isTouched: !!formik.touched.username,
        isValid: !formik.errors.username && formik.touched.username,
      },
      firstname: {
        value: formik.values.firstname,
        hasError: !!formik.errors.firstname,
        isTouched: !!formik.touched.firstname,
        isValid: !formik.errors.firstname && formik.touched.firstname,
      },
      secondname: {
        value: formik.values.secondname,
        hasError: !!formik.errors.secondname,
        isTouched: !!formik.touched.secondname,
        isValid: !formik.errors.secondname && formik.touched.secondname,
      },
      email: {
        value: formik.values.email,
        hasError: !!formik.errors.email,
        isTouched: !!formik.touched.email,
        isValid: !formik.errors.email && formik.touched.email,
      },
      password: {
        value: formik.values.password ? '***' : '',
        hasError: !!formik.errors.password,
        isTouched: !!formik.touched.password,
        isValid: !formik.errors.password && formik.touched.password,
      },
      changepassword: {
        value: formik.values.changepassword ? '***' : '',
        hasError: !!formik.errors.changepassword,
        isTouched: !!formik.touched.changepassword,
        isValid: !formik.errors.changepassword && formik.touched.changepassword,
      },
      countryId: {
        value: formik.values.countryId,
        hasError: !!formik.errors.countryId,
        isTouched: !!formik.touched.countryId,
        isValid: !formik.errors.countryId && formik.touched.countryId,
      },
      streetaddress: {
        value: formik.values.streetaddress,
        hasError: !!formik.errors.streetaddress,
        isTouched: !!formik.touched.streetaddress,
        isValid: !formik.errors.streetaddress && formik.touched.streetaddress,
      },
      suite: {
        value: formik.values.suite,
        hasError: !!formik.errors.suite,
        isTouched: !!formik.touched.suite,
        isValid: !formik.errors.suite && formik.touched.suite,
      },
      cityaddress: {
        value: formik.values.cityaddress,
        hasError: !!formik.errors.cityaddress,
        isTouched: !!formik.touched.cityaddress,
        isValid: !formik.errors.cityaddress && formik.touched.cityaddress,
      },
      postalcode: {
        value: formik.values.postalcode,
        hasError: !!formik.errors.postalcode,
        isTouched: !!formik.touched.postalcode,
        isValid: !formik.errors.postalcode && formik.touched.postalcode,
      },
      birth: {
        value: formik.values.birth,
        hasError: !!formik.errors.birth,
        isTouched: !!formik.touched.birth,
        isValid: !formik.errors.birth && formik.touched.birth,
      },
    }

    console.log('Field-by-field validation:', fieldChecks)

    // List missing/invalid fields
    const invalidFields = Object.entries(fieldChecks)
      .filter(([key, check]) => !check.isValid && check.isTouched)
      .map(([key]) => key)

    const untouchedFields = Object.entries(fieldChecks)
      .filter(([key, check]) => !check.isTouched)
      .map(([key]) => key)

    console.log('Invalid fields (touched but have errors):', invalidFields)
    console.log('Untouched fields:', untouchedFields)
    console.log('Formik isValid:', formik.isValid)
    console.log('Custom validation check will run on next render')
    console.log('============================')
  }, [formik.isValid, formik.values, formik.errors, formik.touched])

  const checkInformation = async (values: any) => {
    let curAddUserName = values.username as string
    curAddUserName = curAddUserName.toLowerCase()

    const valid = await fetch(BACKEND_API_V2 + '/webshop/user/check', {
      method: 'POST',
      body: JSON.stringify({
        name: values.username,
        email: values.email,
      }),
    })
      .then((res) => {
        return res.json()
      })
      .catch((err) => {
        console.log(err)
      })
    if (!valid) {
      swal({
        title: t('SHOPPINGCART_CONTENT_14'),
        text: t('SHOPPINGCART_CONTENT_15'),
        icon: 'error',
      })
      return
    }
    if (valid.word) {
      swal({
        title: t('SHOPPINGCART_CONTENT_16'),
        text: `${t('SHOPPINGCART_CONTENT_17')} '${valid.word}' ${t('SHOPPINGCART_CONTENT_18')}`,
        icon: 'error',
      })
      return
    }
    if (valid.user_name) {
      // Duplicate username — surface it HERE (data-entry) with a clear message, not a generic error
      // at the final sign_up (which the webshop flow can't return from).
      swal({
        title: t('SHOPPINGCART_CONTENT_16'),
        text: valid.message || 'This username is already taken.',
        icon: 'error',
      })
      return
    }
    if (valid.email) {
      swal({
        title: t('SHOPPINGCART_CONTENT_16'),
        text: t('SHOPPINGCART_CONTENT_19'),
        icon: 'error',
      })
      return
    }
    if (valid.status === 'error') {
      swal({
        title: t('SHOPPINGCART_CONTENT_16'),
        text: t('SHOPPINGCART_CONTENT_20'),
        icon: 'error',
      })
      return
    }
    dispatch(setUserInformation(values))
    setViewStatus(4)
  }

  const updateTimezone = (event: React.ChangeEvent<HTMLSelectElement>) => {
    formik.values.timezone = Timezone[event.target.selectedIndex]
  }
  const updateCountry = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const countryId = parseInt(event.target.selectedOptions[0].value)
    const countryName = event.target.selectedOptions[0].text
    formik.setFieldValue('country', countryName)
    formik.setFieldTouched('country', true)
    formik.setFieldValue('countryId', countryId)
    formik.setFieldTouched('countryId', true)
  }

  // Same lookup paymentinfo.tsx already uses (`countrieSettings[].country_code`) to turn the
  // selected operating-country id into the ISO code the VIES lookup needs — no separate
  // id→ISO mapping to maintain.
  const selectedCountryCode: string | null =
    curCountries.find((c) => Number(c.id) === formik.values.countryId)?.country_code ?? null

  const handleMemberTypeChange = (memberType: MemberType) => {
    formik.setFieldValue('memberType', memberType)
    formik.setFieldTouched('memberType', true)
    if (memberType === 'personal') {
      // Clear any business data so a member who picks Business, types a UST-ID, then switches
      // back to Personal never carries stale company/VAT data forward to paymentinfo.
      formik.setFieldValue('company_name', '')
      formik.setFieldValue('vat_id', '')
      formik.setFieldValue('vatStatus', 'idle')
      formik.setFieldValue('vatChecking', false)
    }
  }

  const handleVatStatusChange = (status: VatCheckStatus, inFlight: boolean) => {
    formik.setFieldValue('vatStatus', status)
    formik.setFieldValue('vatChecking', inFlight)
  }

  // Proper validation function that checks all required fields
  const isFormValid = () => {
    const values = formik.values
    const errors = formik.errors

    // Check all required fields have values and no errors
    const requiredFields = {
      username: values.username && values.username.trim().length >= 6 && !errors.username,
      firstname: values.firstname && values.firstname.trim().length > 0 && !errors.firstname,
      secondname: values.secondname && values.secondname.trim().length > 0 && !errors.secondname,
      email: values.email && values.email.trim().length > 0 && !errors.email,
      password: values.password && values.password.length >= 8 && !errors.password,
      changepassword:
        values.changepassword && values.changepassword.length > 0 && !errors.changepassword,
      country: values.country && values.country.trim().length > 0 && !errors.country,
      countryId: values.countryId > 0 && !errors.countryId,
      streetaddress:
        values.streetaddress && values.streetaddress.trim().length >= 3 && !errors.streetaddress,
      suite: values.suite && values.suite.trim().length > 0 && !errors.suite,
      cityaddress:
        values.cityaddress && values.cityaddress.trim().length >= 3 && !errors.cityaddress,
      postalcode: values.postalcode && values.postalcode.trim().length >= 3 && !errors.postalcode,
      birth: values.birth && values.birth !== '2022-01-01' && !errors.birth,
      // Business account: company name + UST-ID are required, and the VIES check must not be
      // blocking (invalid / still in-flight). Mirrors the Yup schema's conditional business rules
      // so the button reflects them immediately on the memberType switch, not a validation tick later.
      ...(values.memberType === 'business'
        ? {
            company_name:
              !!values.company_name &&
              values.company_name.trim().length > 0 &&
              !errors.company_name,
            vat_id: !!values.vat_id && values.vat_id.trim().length > 0 && !errors.vat_id,
            vatStatus: !vatFieldState(values.vatStatus, Boolean(values.vatChecking)).blocks,
          }
        : {}),
    }

    const isValid = Object.values(requiredFields).every((field) => field === true)

    console.log('=== CUSTOM VALIDATION CHECK ===')
    console.log('Required fields status:', requiredFields)
    console.log('Form is valid:', isValid)
    console.log('Formik isValid:', formik.isValid)

    return isValid && formik.isValid
  }

  return (
    <div className='card p-lg-5 shadow-sm'>
      <div className='d-flex flex-column'>
        <form
          className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
          noValidate
          id='kt_login_signup_form'
          onSubmit={formik.handleSubmit}
        >
          <div className='modal-header'>
            <h3 className='fw-bolder m-0'>{t('SHOPPINGCART_CONTENT_16')}</h3>
          </div>
          <div className='modal-body py-10'>
            <div className='scroll-y me-n7 pe-7'>
              {/* Personal / business selector + company name + UST-ID (inline VIES), at the TOP so
                  account type is chosen first. Shown ONLY when VAT is live (backend `vat_active`);
                  off → the signup form looks exactly as it did pre-VAT (no account-type selector). */}
              {state.vatEstimate?.vat_active === true && (
              <BusinessVatFields
                memberType={formik.values.memberType}
                onMemberTypeChange={handleMemberTypeChange}
                companyName={formik.values.company_name}
                onCompanyNameChange={(value) => {
                  formik.setFieldValue('company_name', value)
                  formik.setFieldTouched('company_name', true)
                }}
                companyNameError={formik.errors.company_name as string | undefined}
                companyNameTouched={formik.touched.company_name}
                vatId={formik.values.vat_id}
                onVatIdChange={(value) => {
                  formik.setFieldValue('vat_id', value)
                  formik.setFieldTouched('vat_id', true)
                }}
                vatIdError={formik.errors.vat_id as string | undefined}
                vatIdTouched={formik.touched.vat_id}
                countryCode={selectedCountryCode}
                onVatStatusChange={handleVatStatusChange}
              />
              )}
              {/* Username */}
              <div className='row mb-6'>
                <label className='col-lg-3 col-form-label required fw-bold fs-6'>
                  {t('TEXT_USER_NAME')}
                </label>
                <div className='col-lg-9 fv-row'>
                  <input
                    type='text'
                    placeholder={t('TEXT_USER_NAME')}
                    onKeyPress={(e) => {
                      if (new RegExp(/[a-zA-Z0-9!@#$%^&*+]/).test(e.key)) {
                      } else e.preventDefault()
                    }}
                    {...formik.getFieldProps('username')}
                    onInput={(e) => {
                      const target = e.target as HTMLInputElement
                      console.log('Username input event:', target.value)
                      formik.setFieldValue('username', target.value)
                      formik.setFieldTouched('username', true)
                    }}
                    onPaste={(e) => {
                      console.log('Username paste event detected')
                      setTimeout(() => {
                        const target = e.target as HTMLInputElement
                        formik.setFieldValue('username', target.value)
                        formik.setFieldTouched('username', true)
                        formik.validateField('username')
                      }, 0)
                    }}
                    className={clsx(
                      'form-control form-control-lg form-control-solid',
                      {
                        'is-invalid': formik.touched.username && formik.errors.username,
                      },
                      {
                        'is-valid': formik.touched.username && !formik.errors.username,
                      }
                    )}
                  />
                  {formik.touched.username && formik.errors.username && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.username}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {/* Full Name */}
              <div className='row mb-6'>
                <label className='col-lg-3 col-form-label required fw-bold fs-6'>
                  {t('ACCOUNT_OVERVIEW_DESC_FULLNAME')}
                </label>
                <div className='col-lg-9'>
                  <div className='row'>
                    <div className='col-lg-6 mb-3 fv-row'>
                      <input
                        placeholder={t('ACCOUNT_OVERVIEW_DESC_FULLNAME')}
                        type='text'
                        {...formik.getFieldProps('firstname')}
                        onInput={(e) => {
                          const target = e.target as HTMLInputElement
                          console.log('Firstname input event:', target.value)
                          formik.setFieldValue('firstname', target.value)
                          formik.setFieldTouched('firstname', true)
                        }}
                        onPaste={(e) => {
                          console.log('Firstname paste event detected')
                          setTimeout(() => {
                            const target = e.target as HTMLInputElement
                            formik.setFieldValue('firstname', target.value)
                            formik.setFieldTouched('firstname', true)
                            formik.validateField('firstname')
                          }, 0)
                        }}
                        className={clsx(
                          'form-control form-control-lg form-control-solid',
                          {
                            'is-invalid': formik.touched.firstname && formik.errors.firstname,
                          },
                          {
                            'is-valid': formik.touched.firstname && !formik.errors.firstname,
                          }
                        )}
                      />
                      {formik.touched.firstname && formik.errors.firstname && (
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block'>
                            <span role='alert'>{formik.errors.firstname}</span>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className='col-lg-6 mb-3 fv-row'>
                      <input
                        placeholder={t('ACCOUNT_BILLING_ADDRESS_DLG_LASTNAME')}
                        type='text'
                        {...formik.getFieldProps('secondname')}
                        onInput={(e) => {
                          const target = e.target as HTMLInputElement
                          console.log('Secondname input event:', target.value)
                          formik.setFieldValue('secondname', target.value)
                          formik.setFieldTouched('secondname', true)
                        }}
                        onPaste={(e) => {
                          console.log('Secondname paste event detected')
                          setTimeout(() => {
                            const target = e.target as HTMLInputElement
                            formik.setFieldValue('secondname', target.value)
                            formik.setFieldTouched('secondname', true)
                            formik.validateField('secondname')
                          }, 0)
                        }}
                        className={clsx(
                          'form-control form-control-lg form-control-solid',
                          {
                            'is-invalid': formik.touched.secondname && formik.errors.secondname,
                          },
                          {
                            'is-valid': formik.touched.secondname && !formik.errors.secondname,
                          }
                        )}
                      />
                      {formik.touched.secondname && formik.errors.secondname && (
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block'>
                            <span role='alert'>{formik.errors.secondname}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              {/* Email */}
              <div className='row mb-6'>
                <label className='col-lg-3 col-form-label required fw-bold fs-6'>
                  {t('TEXT_EMAIL')}
                </label>
                <div className='col-lg-9'>
                  <input
                    placeholder={t('TEXT_EMAIL')}
                    type='email'
                    {...formik.getFieldProps('email')}
                    onInput={(e) => {
                      const target = e.target as HTMLInputElement
                      console.log('Email input event:', target.value)
                      formik.setFieldValue('email', target.value)
                      formik.setFieldTouched('email', true)
                    }}
                    onPaste={(e) => {
                      console.log('Email paste event detected')
                      setTimeout(() => {
                        const target = e.target as HTMLInputElement
                        formik.setFieldValue('email', target.value)
                        formik.setFieldTouched('email', true)
                        formik.validateField('email')
                      }, 0)
                    }}
                    className={clsx(
                      'form-control form-control-lg form-control-solid',
                      {'is-invalid': formik.touched.email && formik.errors.email},
                      {
                        'is-valid': formik.touched.email && !formik.errors.email,
                      }
                    )}
                  />
                  {formik.touched.email && formik.errors.email && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.email}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {/* Password */}
              <div className='row mb-6'>
                <label className='col-lg-3 col-form-label required fw-bold fs-6'>
                  {t('ACCOUNT_SECURITY_SIGNIN_PASSWORD_DESC1')}
                </label>
                <div className='col-lg-9 fv-row'>
                  <div style={{display: 'flex', alignItems: 'center'}}>
                    <input
                      type={showPasswordConfirm ? 'text' : 'password'}
                      placeholder={t('ACCOUNT_SECURITY_SIGNIN_PASSWORD_DESC1')}
                      style={{marginRight: '10px'}}
                      {...formik.getFieldProps('password')}
                      onInput={(e) => {
                        const target = e.target as HTMLInputElement
                        console.log('Password input event detected')
                        formik.setFieldValue('password', target.value)
                        formik.setFieldTouched('password', true)
                      }}
                      onPaste={(e) => {
                        console.log('Password paste event detected')
                        setTimeout(() => {
                          const target = e.target as HTMLInputElement
                          formik.setFieldValue('password', target.value)
                          formik.setFieldTouched('password', true)
                          formik.validateField('password')
                        }, 0)
                      }}
                      className={clsx(
                        'form-control form-control-lg form-control-solid',
                        {
                          'is-invalid': formik.touched.password && formik.errors.password,
                        },
                        {
                          'is-valid': formik.touched.password && !formik.errors.password,
                        }
                      )}
                      // data-tip="Must contain at least one uppercase and lowercase letter, a number and at least one special character.   Special characters: !#$%&'()*+,-./:;<=>?@[]^_`{|}~ "
                    />
                    {/* <ReactTooltip
                      place='bottom'
                      type='info'
                      effect='solid'
                      className='register-tooltip'
                    /> */}
                    <i
                      className={showPasswordConfirm ? 'far fa-eye-slash' : 'far fa-eye'}
                      id='togglePassword'
                      style={{cursor: 'pointer'}}
                      onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                    />
                  </div>
                  {formik.touched.password && formik.errors.password && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.password}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {/* Confirm password */}
              <div className='row mb-6'>
                <label className='col-lg-3 col-form-label required fw-bold fs-6'>
                  {t('ACCOUNT_SECURITY_SIGNIN_EMAIL_DESC3')}
                </label>
                <div className='col-lg-9'>
                  <div style={{display: 'flex', alignItems: 'center'}}>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      placeholder={t('SHOPPINGCART_CONTENT_21')}
                      style={{marginRight: '10px'}}
                      {...formik.getFieldProps('changepassword')}
                      onInput={(e) => {
                        const target = e.target as HTMLInputElement
                        console.log('Confirm password input event detected')
                        formik.setFieldValue('changepassword', target.value)
                        formik.setFieldTouched('changepassword', true)
                      }}
                      onPaste={(e) => {
                        console.log('Confirm password paste event detected')
                        setTimeout(() => {
                          const target = e.target as HTMLInputElement
                          formik.setFieldValue('changepassword', target.value)
                          formik.setFieldTouched('changepassword', true)
                          formik.validateField('changepassword')
                        }, 0)
                      }}
                      className={clsx(
                        'form-control form-control-lg form-control-solid',
                        {
                          'is-invalid':
                            formik.touched.changepassword && formik.errors.changepassword,
                        },
                        {
                          'is-valid':
                            formik.touched.changepassword && !formik.errors.changepassword,
                        }
                      )}
                    />
                    <i
                      className={showPassword ? 'far fa-eye-slash' : 'far fa-eye'}
                      id='togglePassword'
                      style={{cursor: 'pointer'}}
                      onClick={() => setShowPassword(!showPassword)}
                    />
                  </div>
                  {formik.touched.changepassword && formik.errors.changepassword && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.changepassword}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {/* UserAddress */}
              <div className='row mb-6'>
                <label className='col-lg-3 col-form-label required fw-bold fs-6'>
                  {t('SHOPPINGCART_CONTENT_22')}
                </label>
                <div className='col-lg-9 mb-3'>
                  <select
                    className='form-select form-select-lg form-select-solid'
                    data-control='select2'
                    data-placeholder='Select Country...'
                    value={formik.values.countryId}
                    onChange={updateCountry}
                  >
                    {localizedCountries.map((item, i) => (
                      <option value={item?.id} key={i}>
                        {item.display_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className='d-flex'>
                  <div className='col-lg-3'></div>
                  <div className='person-street d-flex'>
                    <div className='person-street-item1 mb-3'>
                      <input
                        type='text'
                        placeholder={t('ACCOUNT_OVERVIEW_DESC_STREET')}
                        {...formik.getFieldProps('streetaddress')}
                        onInput={(e) => {
                          const target = e.target as HTMLInputElement
                          console.log('Street address input event:', target.value)
                          formik.setFieldValue('streetaddress', target.value)
                          formik.setFieldTouched('streetaddress', true)
                        }}
                        onPaste={(e) => {
                          console.log('Street address paste event detected')
                          setTimeout(() => {
                            const target = e.target as HTMLInputElement
                            formik.setFieldValue('streetaddress', target.value)
                            formik.setFieldTouched('streetaddress', true)
                            formik.validateField('streetaddress')
                          }, 0)
                        }}
                        className={clsx(
                          'form-control form-control-lg form-control-solid',
                          {
                            'is-invalid':
                              formik.touched.streetaddress && formik.errors.streetaddress,
                          },
                          {
                            'is-valid':
                              formik.touched.streetaddress && !formik.errors.streetaddress,
                          }
                        )}
                      />
                      {formik.touched.streetaddress && formik.errors.streetaddress && (
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block'>
                            <span role='alert'>{formik.errors.streetaddress}</span>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className='person-street-item2 mb-3'>
                      <input
                        type='text'
                        placeholder={t('ACCOUNT_BILLING_ADDRESS_DLG_SUITE2')}
                        {...formik.getFieldProps('suite')}
                        onInput={(e) => {
                          const target = e.target as HTMLInputElement
                          console.log('Suite input event:', target.value)
                          formik.setFieldValue('suite', target.value)
                          formik.setFieldTouched('suite', true)
                        }}
                        onPaste={(e) => {
                          console.log('Suite paste event detected')
                          setTimeout(() => {
                            const target = e.target as HTMLInputElement
                            formik.setFieldValue('suite', target.value)
                            formik.setFieldTouched('suite', true)
                            formik.validateField('suite')
                          }, 0)
                        }}
                        className={clsx(
                          'form-control form-control-lg form-control-solid',
                          {
                            'is-invalid': formik.touched.suite && formik.errors.suite,
                          },
                          {
                            'is-valid': formik.touched.suite && !formik.errors.suite,
                          }
                        )}
                      />
                      {/* {formik.touched.suite && formik.errors.suite && (
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block'>
                            <span role='alert'>{formik.errors.suite}</span>
                          </div>
                        </div>
                      )} */}
                    </div>
                  </div>
                </div>
                <div className='d-flex'>
                  <div className='col-lg-3'></div>
                  <div className='person-street d-flex'>
                    <div className='person-zip-item1 mb-3'>
                      <input
                        type='text'
                        placeholder={t('ACCOUNT_OVERVIEW_DESC_ZIPCODE')}
                        {...formik.getFieldProps('postalcode')}
                        onInput={(e) => {
                          const target = e.target as HTMLInputElement
                          console.log('Postal code input event:', target.value)
                          formik.setFieldValue('postalcode', target.value)
                          formik.setFieldTouched('postalcode', true)
                        }}
                        onPaste={(e) => {
                          console.log('Postal code paste event detected')
                          setTimeout(() => {
                            const target = e.target as HTMLInputElement
                            formik.setFieldValue('postalcode', target.value)
                            formik.setFieldTouched('postalcode', true)
                            formik.validateField('postalcode')
                          }, 0)
                        }}
                        className={clsx(
                          'form-control form-control-lg form-control-solid',
                          {
                            'is-invalid': formik.touched.postalcode && formik.errors.postalcode,
                          },
                          {
                            'is-valid': formik.touched.postalcode && !formik.errors.postalcode,
                          }
                        )}
                      />
                      {formik.touched.postalcode && formik.errors.postalcode && (
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block'>
                            <span role='alert'>{formik.errors.postalcode}</span>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className='person-zip-item2 mb-3'>
                      <input
                        type='text'
                        placeholder={t('ACCOUNT_OVERVIEW_DESC_CITY')}
                        {...formik.getFieldProps('cityaddress')}
                        onInput={(e) => {
                          const target = e.target as HTMLInputElement
                          console.log('City input event:', target.value)
                          formik.setFieldValue('cityaddress', target.value)
                          formik.setFieldTouched('cityaddress', true)
                        }}
                        onPaste={(e) => {
                          console.log('City paste event detected')
                          setTimeout(() => {
                            const target = e.target as HTMLInputElement
                            formik.setFieldValue('cityaddress', target.value)
                            formik.setFieldTouched('cityaddress', true)
                            formik.validateField('cityaddress')
                          }, 0)
                        }}
                        className={clsx(
                          'form-control form-control-lg form-control-solid',
                          {
                            'is-invalid': formik.touched.cityaddress && formik.errors.cityaddress,
                          },
                          {
                            'is-valid': formik.touched.cityaddress && !formik.errors.cityaddress,
                          }
                        )}
                      />
                      {formik.touched.cityaddress && formik.errors.cityaddress && (
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block'>
                            <span role='alert'>{formik.errors.cityaddress}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              {/* Time Zone - Hidden during signup */}
              {/* Timezone field is hidden but value is still sent via formik initialValues */}
              {/* Birthday */}
              <div className='row mb-6'>
                <label className='col-lg-3 col-form-label required fw-bold fs-6'>
                  {t('ACCOUNT_OVERVIEW_DESC_BIRTH')}
                </label>
                <div className='col-lg-9'>
                  <div className='col'>
                    <div className='fv-row mb-9'>
                      <input
                        type='date'
                        placeholder={t('SHOPPINGCART_CONTENT_23')}
                        id='kt_calendar_datepicker_end_date'
                        {...formik.getFieldProps('birth')}
                        required
                        onInput={(e) => {
                          const target = e.target as HTMLInputElement
                          console.log('Birth date input event:', target.value)
                          formik.setFieldValue('birth', target.value)
                          formik.setFieldTouched('birth', true)
                        }}
                        onChange={(e) => {
                          console.log('Birth date change event:', e.target.value)
                          formik.setFieldValue('birth', e.target.value)
                          formik.setFieldTouched('birth', true)
                        }}
                        className={clsx(
                          'form-control form-control-lg form-control-solid',
                          {'is-invalid': formik.touched.birth && formik.errors.birth},
                          {
                            'is-valid': formik.touched.birth && !formik.errors.birth,
                          }
                        )}
                      />
                      {formik.touched.birth && formik.errors.birth && (
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block'>
                            <span role='alert'>{formik.errors.birth}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className='modal-footer flex-center'>
            <button
              type='submit'
              className='btn btn-primary'
              id='kt_account_profile_details_submit'
              disabled={!isFormValid()}
              onClick={(e) => {
                console.log('=== BUTTON CLICKED ===')
                console.log('Form is valid (custom):', isFormValid())
                console.log('Formik isValid:', formik.isValid)
                console.log('Form errors:', formik.errors)
                console.log('Form values:', formik.values)
                if (!isFormValid()) {
                  e.preventDefault()
                  console.log('Form submission prevented - form is not valid')
                }
              }}
            >
              {t('BTN_TEXT_CONTINUE')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
